import { useState, useCallback } from "react";
import type { ChatMessage } from "../types";

interface MessageBubbleProps {
  msg: ChatMessage;
  onApproveTools?: (ids: string[]) => void;
}

// Tool icon mapping for enhanced display
const TOOL_ICONS: Record<string, string> = {
  github_repo_tree: "📂", github_read_file: "📄", github_search_code: "🔍",
  github_create_branch: "🌿", github_commit_file: "✨", github_create_pr: "🚀",
  github_diff: "📊", grep_search: "🔍", git_status: "📌",
  run_tests: "🧪", run_build: "🛠️", shell_exec: "💻",
  file_read: "📄", file_write: "✏️", web_fetch: "🌐",
};

export function MessageBubble({ msg, onApproveTools }: MessageBubbleProps) {
  const isUser = msg.role === "user";
  const [toolsExpanded, setToolsExpanded] = useState(false);

  return (
    <div className={`flex flex-col mb-4 ${isUser ? "items-end" : "items-start"}`}>
      {/* User image attachments */}
      {isUser && msg.attachments?.length ? (
        <div className="flex gap-1.5 mb-1.5 flex-wrap justify-end">
          {msg.attachments
            .filter((a) => a.type.startsWith("image/"))
            .map((att, i) => (
              <img
                key={i}
                src={`data:${att.type};base64,${att.data}`}
                alt={att.name}
                className="max-h-28 rounded-lg border border-border"
              />
            ))}
        </div>
      ) : null}

      {/* Message bubble */}
      <div
        className={`
          max-w-[90%] sm:max-w-[80%] px-3.5 py-2.5 rounded-xl text-sm leading-relaxed
          ${isUser
            ? "bg-primary text-primary-foreground"
            : msg.pending_approval
              ? "bg-card border border-warning text-card-foreground"
              : "bg-card border border-border text-card-foreground"
          }
        `}
      >
        <MarkdownContent content={msg.content} />

        {/* Tool approval UI — full-width on mobile */}
        {msg.pending_approval && msg.tool_calls?.length ? (
          <div className="mt-3 p-3 sm:p-3 bg-background rounded-lg border border-warning">
            <div className="text-xs font-semibold text-warning mb-2 flex items-center gap-1.5">
              <span className="w-2 h-2 bg-warning rounded-full animate-pulse" />
              Approval required
            </div>
            {msg.tool_calls.map((tc) => (
              <div key={tc.id} className="mb-3 last:mb-0">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-sm">{TOOL_ICONS[tc.name] || "🔧"}</span>
                  <code className="text-primary text-xs font-semibold">{tc.name}</code>
                </div>
                <pre className="text-xs text-muted-foreground mt-1 overflow-auto max-h-32 whitespace-pre-wrap bg-card p-2 rounded border border-border">
                  {JSON.stringify(tc.arguments, null, 2)}
                </pre>
              </div>
            ))}
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => onApproveTools?.(msg.tool_calls!.map((tc) => tc.id))}
                className="flex-1 sm:flex-none px-4 py-2.5 sm:py-1.5 bg-success text-success-foreground text-xs font-medium rounded-md hover:bg-success/90 transition-colors"
              >
                ✓ Approve & Execute
              </button>
              <button
                onClick={() => {}}
                className="flex-1 sm:flex-none px-4 py-2.5 sm:py-1.5 bg-card border border-border text-muted-foreground text-xs font-medium rounded-md hover:bg-card/80 transition-colors"
              >
                Skip
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Tools used (accordion-style) */}
      {!isUser && msg.tools_used?.length ? (
        <div className="mt-1 max-w-[90%] sm:max-w-[80%] w-full">
          <button
            onClick={() => setToolsExpanded(!toolsExpanded)}
            className="flex items-center gap-1.5 text-primary text-xs hover:underline"
          >
            <span className={`transition-transform ${toolsExpanded ? "rotate-90" : ""}`}>▶</span>
            {msg.tools_used.length} tool{msg.tools_used.length > 1 ? "s" : ""} used
            {msg.usage?.tool_iterations ? ` (${msg.usage.tool_iterations} iterations)` : ""}
          </button>
          {toolsExpanded && (
            <div className="mt-1.5 space-y-1">
              {msg.tools_used.map((t, i) => (
                <ToolResultCard key={i} tool={t} />
              ))}
            </div>
          )}
        </div>
      ) : null}

      {/* Assistant metadata */}
      {!isUser && msg.route && (
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <ModelBadge route={msg.route} />
          {msg.cost && <CostBadge cost={msg.cost.response_cost_usd} />}
          {msg.usage && (
            <span className="text-xs text-muted-foreground font-mono">
              {msg.usage.total_tokens} tok {msg.latency_ms && `/ ${msg.latency_ms}ms`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Enhanced tool result card ────────────────────────────────

function ToolResultCard({ tool }: { tool: { tool: string; args: any; result_preview: string } }) {
  const [expanded, setExpanded] = useState(false);
  const icon = TOOL_ICONS[tool.tool] || "🔧";

  // Detect special tool types for rich rendering
  const isDiff = tool.tool === "github_diff";
  const isFileRead = tool.tool === "github_read_file" || tool.tool === "file_read";
  const isShell = tool.tool === "shell_exec" || tool.tool === "run_tests" || tool.tool === "run_build";
  const isGrep = tool.tool === "grep_search";

  // Try to parse JSON result
  let parsed: any = null;
  try { parsed = JSON.parse(tool.result_preview); } catch {}

  return (
    <div className="bg-card rounded-lg border border-border text-xs">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-card/80 transition-colors text-left"
      >
        <span>{icon}</span>
        <code className="text-success font-medium">{tool.tool}</code>
        {tool.args?.repo && <span className="text-muted-foreground">{tool.args.repo}</span>}
        {tool.args?.path && <span className="text-muted-foreground truncate max-w-[200px]">/{tool.args.path}</span>}
        <span className={`ml-auto transition-transform ${expanded ? "rotate-90" : ""}`}>▶</span>
      </button>

      {expanded && (
        <div className="px-2.5 pb-2 overflow-x-auto">
          {/* Diff view */}
          {isDiff && parsed?.files ? (
            <div className="space-y-2">
              <div className="text-muted-foreground">
                {parsed.files_changed} files, +{parsed.files.reduce((s: number, f: any) => s + (f.additions || 0), 0)}
                / -{parsed.files.reduce((s: number, f: any) => s + (f.deletions || 0), 0)}
              </div>
              {parsed.files.map((f: any, i: number) => (
                <div key={i}>
                  <div className="font-medium text-foreground flex items-center gap-1">
                    <span className={f.status === "added" ? "text-green-400" : f.status === "removed" ? "text-red-400" : "text-yellow-400"}>●</span>
                    {f.filename}
                    <span className="text-muted-foreground font-normal">+{f.additions} -{f.deletions}</span>
                  </div>
                  {f.patch && <DiffBlock patch={f.patch} />}
                </div>
              ))}
            </div>
          ) : isShell && parsed ? (
            /* Terminal output */
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${parsed.exit_code === 0 || parsed.passed ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                  {parsed.exit_code === 0 || parsed.passed ? "PASS" : `EXIT ${parsed.exit_code}`}
                </span>
                {parsed.duration_ms && <span className="text-muted-foreground">{parsed.duration_ms}ms</span>}
              </div>
              <pre className="bg-background p-2 rounded overflow-x-auto text-[11px] font-mono text-muted-foreground max-h-60">
                {parsed.stdout || parsed.stderr || "(no output)"}
              </pre>
            </div>
          ) : isGrep && parsed?.results ? (
            /* Grep results */
            <div className="space-y-0.5">
              <div className="text-muted-foreground mb-1">{parsed.total_matches} matches for "{parsed.pattern}"</div>
              {parsed.results.slice(0, 20).map((r: any, i: number) => (
                <div key={i} className="font-mono flex gap-2">
                  <span className="text-primary shrink-0">{r.file?.replace("/home/git/", "")}:{r.line}</span>
                  <span className="text-muted-foreground truncate">{r.text}</span>
                </div>
              ))}
            </div>
          ) : (
            /* Default: raw preview */
            <pre className="bg-background p-2 rounded overflow-x-auto text-[11px] font-mono text-muted-foreground max-h-40 whitespace-pre-wrap">
              {tool.result_preview.slice(0, 2000)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// ── Diff renderer ────────────────────────────────────────────

function DiffBlock({ patch }: { patch: string }) {
  const lines = patch.split("\n").slice(0, 100);
  return (
    <pre className="bg-background p-2 rounded overflow-x-auto text-[11px] font-mono mt-1 max-h-48">
      {lines.map((line, i) => {
        let cls = "text-muted-foreground";
        if (line.startsWith("+") && !line.startsWith("+++")) cls = "text-green-400 bg-green-500/10";
        else if (line.startsWith("-") && !line.startsWith("---")) cls = "text-red-400 bg-red-500/10";
        else if (line.startsWith("@@")) cls = "text-blue-400";
        return <div key={i} className={cls}>{line}</div>;
      })}
    </pre>
  );
}

// ── Markdown renderer with code enhancements ─────────────────

function MarkdownContent({ content }: { content: string }) {
  if (!content) return null;

  // Split by code blocks first
  const parts = content.split(/(```[\s\S]*?```)/g);

  return (
    <div className="space-y-2">
      {parts.map((part, i) => {
        if (part.startsWith("```")) {
          const match = part.match(/```([\w./-]*)\n?([\s\S]*?)```/);
          const language = match?.[1] || "";
          const code = (match?.[2] || part.slice(3, -3)).trim();
          const isDiff = language === "diff" || code.split("\n").some(l => /^[+-](?![-+])/.test(l) && code.includes("@@"));

          return <CodeBlock key={i} code={code} language={language} isDiff={isDiff} />;
        }

        // Process inline markdown
        return (
          <div key={i}>
            {part.split("\n").map((line, j) => (
              <p key={j} className="mb-1 last:mb-0">
                <InlineMarkdown text={line} />
              </p>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ── Code block with copy, line numbers, diff coloring ────────

function CodeBlock({ code, language, isDiff }: { code: string; language: string; isDiff: boolean }) {
  const [copied, setCopied] = useState(false);
  const lines = code.split("\n");
  const showLineNums = lines.length > 3;

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [code]);

  return (
    <div className="relative group">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-background rounded-t-lg border-b border-border px-3 py-1">
        <span className="text-[10px] text-muted-foreground font-mono">{language || "code"}</span>
        <button
          onClick={handleCopy}
          className="text-[10px] text-muted-foreground hover:text-foreground transition-colors px-1.5 py-0.5 rounded hover:bg-card"
        >
          {copied ? "✓ Copied" : "Copy"}
        </button>
      </div>
      {/* Code body */}
      <pre className="bg-background p-3 rounded-b-lg overflow-x-auto text-[11px] font-mono leading-[1.6]">
        {lines.map((line, i) => {
          let cls = "";
          if (isDiff) {
            if (line.startsWith("+") && !line.startsWith("+++")) cls = "text-green-400 bg-green-500/10";
            else if (line.startsWith("-") && !line.startsWith("---")) cls = "text-red-400 bg-red-500/10";
            else if (line.startsWith("@@")) cls = "text-blue-400";
          }
          return (
            <div key={i} className={`${cls} flex`}>
              {showLineNums && (
                <span className="select-none text-muted-foreground/40 w-8 shrink-0 text-right pr-3">{i + 1}</span>
              )}
              <span>{line || " "}</span>
            </div>
          );
        })}
      </pre>
    </div>
  );
}

function InlineMarkdown({ text }: { text: string }) {
  if (!text.trim()) return <br />;

  // Process inline code first
  const parts = text.split(/(`[^`]+`)/g);

  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("`") && part.endsWith("`")) {
          return (
            <code key={i} className="px-1 py-0.5 bg-background rounded text-xs font-mono text-primary">
              {part.slice(1, -1)}
            </code>
          );
        }

        // Bold
        let processed: React.ReactNode = part;
        processed = processPattern(processed, /\*\*([^*]+)\*\*/g, (match, i) => (
          <strong key={i}>{match[1]}</strong>
        ));

        // Italic
        processed = processPattern(processed, /\*([^*]+)\*/g, (match, i) => (
          <em key={i}>{match[1]}</em>
        ));

        return <span key={i}>{processed}</span>;
      })}
    </>
  );
}

function processPattern(
  input: React.ReactNode,
  pattern: RegExp,
  replacer: (match: RegExpMatchArray, index: number) => React.ReactNode
): React.ReactNode {
  if (typeof input !== "string") return input;

  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let idx = 0;

  const regex = new RegExp(pattern);
  let str = input;

  while ((match = regex.exec(str)) !== null) {
    if (match.index > lastIndex) {
      result.push(str.slice(lastIndex, match.index));
    }
    result.push(replacer(match, idx++));
    lastIndex = match.index + match[0].length;
    str = str.slice(lastIndex);
    lastIndex = 0;
  }

  if (str) {
    result.push(str);
  }

  return result.length === 1 ? result[0] : result;
}

function ModelBadge({ route }: { route: ChatMessage["route"] }) {
  if (!route) return null;

  const colorClass = {
    local: "bg-local/20 text-local",
    edge: "bg-edge/20 text-edge",
    remote: "bg-remote/20 text-remote",
  }[route.location] || "bg-muted/20 text-muted-foreground";

  return (
    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${colorClass}`}>
      {route.display_name}
    </span>
  );
}

function CostBadge({ cost }: { cost: number }) {
  return (
    <span className="text-xs font-mono text-muted-foreground">
      ${cost.toFixed(4)}
    </span>
  );
}
